import { useDeleteSession } from '@actions';
import { ReactComponent as AvatarSkeleton } from '@assets/icons/V4/basic-avatar-skeleton.svg';
import { ReactComponent as SettingsIcon } from '@assets/icons/V4/gear-icon.svg';
import { ReactComponent as DeleteIcon } from '@assets/icons/V4/trash-icon.svg';
import Button from '@components/V4/Button';
import IconButton from '@components/V4/IconButton';
import QualityScoreBadge from '@components/V4/QualityScore/QualityScoreBadge';
import Table from '@components/V4/Table/Table';
import { SessionStatus } from '@shared/enums';
import { Checkbox, Popconfirm } from 'antd';
import useClientPagination from 'apps/agora/src/hooks/useClientPagination';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import React from 'react';
import { useMemo, useState } from 'react';
import AssignSessionsMentorModal from './AssignSessionsMentorModal';

interface SessionsListProps {
  enrollment: { sessions: any[]; mentor: any };
  enrollmentId: string;
  onSessionModalOpen: (session: any) => void;
}

type SessionGroup = { mentorId: string; sessions: any[] };

const isSessionPending = (session: any) =>
  (session.statuses?.[session.statuses.length - 1]?.value ?? SessionStatus.PENDING) ===
  SessionStatus.PENDING;

const SessionsList = (props: SessionsListProps) => {
  const { enrollment, enrollmentId, onSessionModalOpen } = props;

  const { sessions, mentor } = enrollment;
  const toast = useToast();

  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const { mutate: deleteSession } = useDeleteSession({
    onError() {
      toast.error('Slot could not be deleted.');
    },
  });
  const sortedSessions = useMemo(() => {
    const sessionsWithIndexes = sessions.map((session, index) => {
      let relevantMeetingInfo;

      if (mentor || session.relevantMeetingInfo) {
        relevantMeetingInfo = session.relevantMeetingInfo;
      } else {
        relevantMeetingInfo = {
          _id: 'TBD',
          fullName: 'TBD',
        };
      }

      return {
        ...session,
        relevantMeetingInfo,
        index,
      };
    });

    return sessionsWithIndexes.sort((a, b) => a.index - b.index);
  }, [sessions, mentor]);

  const initialPageSize = 10;

  let indexOfFirstRelevantSession =
    sortedSessions.findIndex(
      (session) => session.statuses[session.statuses.length - 1]?.value === SessionStatus.PENDING
    ) - 1;

  // If there's no session w/ a pending status get the index of the last session
  if (indexOfFirstRelevantSession < 0 && sortedSessions.length > 0) {
    indexOfFirstRelevantSession = sortedSessions.length - 1;
  } else if (sortedSessions.length === 0) {
    indexOfFirstRelevantSession = 0;
  }

  const defaultPaginationPage =
    indexOfFirstRelevantSession + 1 <= initialPageSize
      ? 1
      : Math.ceil((indexOfFirstRelevantSession + 1) / initialPageSize);

  const [paginatedSessions, pagination] = useClientPagination({
    data: sortedSessions,
    shouldOnlyShowPages: true,
    defaultPage: defaultPaginationPage,
    initialPageSize: initialPageSize,
  });

  const groupedSessions = useMemo(
    () =>
      (paginatedSessions ?? []).reduce((groups: SessionGroup[], session: any) => {
        const mentorId = session?.relevantMeetingInfo?.mentor?._id ?? 'TBD';
        const currentGroup = groups.at(-1);

        if (currentGroup && currentGroup.mentorId === mentorId) {
          currentGroup.sessions.push(session);
        } else {
          groups.push({ mentorId, sessions: [session] });
        }

        return groups;
      }, []),
    [paginatedSessions]
  );

  const selectableSessions = sortedSessions.filter((session) => isSessionPending(session));

  const selectedSessions = sortedSessions.filter((session) =>
    selectedSessionIds.includes(session._id)
  );

  const toggleSession = (sessionId: string) =>
    setSelectedSessionIds((prev) =>
      prev.includes(sessionId) ? prev.filter((id) => id !== sessionId) : [...prev, sessionId]
    );

  const tableHeaders = ['', 'Session Number', 'Date', 'Feedback', 'Status', 'AI Score', 'Actions'];

  return (
    <>
      {selectableSessions.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-customGrey">
            {selectedSessionIds.length
              ? `${selectedSessionIds.length} session${
                  selectedSessionIds.length === 1 ? '' : 's'
                } selected`
              : 'Select pending sessions to assign them to a different mentor.'}
          </span>
          <div className="flex gap-3">
            {selectedSessionIds.length > 0 && (
              <Button
                variant="text"
                buttonText="Clear"
                onClick={() => setSelectedSessionIds([])}
                size="normal"
              />
            )}
            <Button
              variant="secondary"
              buttonText="Assign Mentor"
              disabled={!selectedSessionIds.length}
              onClick={() => setIsAssignModalOpen(true)}
              size="normal"
            />
          </div>
        </div>
      )}

      <Table size="small" pagination={pagination}>
        <Table.Header>
          <Table.Row isHeader>
            {tableHeaders.map((header, headerIndex) => (
              <Table.HeaderCell key={header || `select-${headerIndex}`}>{header}</Table.HeaderCell>
            ))}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {groupedSessions.map((group: SessionGroup) => (
            <React.Fragment key={`${group.mentorId}-${group.sessions[0]?._id}`}>
              <Table.Row className="border-b-0 border-customgrey border-t-2">
                <Table.Cell colSpan={tableHeaders.length} className="bg-surfaceHover">
                  <div className="flex gap-2">
                    <AvatarSkeleton />
                    {group.sessions[0]?.relevantMeetingInfo?.mentor?.fullName ?? 'No Mentor'}
                    {group.sessions[0]?.assignedMentor?._id
                      ? ' · assigned'
                      : !!group.sessions[0]?.relevantMeetingInfo?.mentor?.fullName &&
                        `; ${moment(enrollment?.mentor?.addedOn).format('DD.MM.YYYY')}`}
                  </div>
                </Table.Cell>
              </Table.Row>
              {group.sessions?.map((session: any) => (
                <Table.Row key={session._id}>
                  <Table.Cell>
                    {isSessionPending(session) && (
                      <Checkbox
                        checked={selectedSessionIds.includes(session._id)}
                        onChange={() => toggleSession(session._id)}
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell onClick={() => onSessionModalOpen(session._id)}>
                    {session.name}
                  </Table.Cell>
                  <Table.Cell>
                    {session.relevantMeetingInfo?.scheduledDate
                      ? moment(session.relevantMeetingInfo?.scheduledDate).format('DD.MM.YYYY')
                      : 'n/a'}
                  </Table.Cell>
                  <Table.Cell
                    className={mergeClassNames({
                      'text-customRed': moment(session.mentorFeedback?.createdAt).isAfter(
                        moment(
                          session.relevantMeetingInfo?.scheduledDate ??
                            session.mentorFeedback?.createdAt
                        ).add(49, 'hours')
                      ),
                    })}
                  >
                    {session.mentorFeedback?.createdAt
                      ? moment(session.mentorFeedback?.createdAt).format('DD.MM.YYYY')
                      : 'n/a'}
                  </Table.Cell>
                  <Table.Cell>
                    {SessionStatus[session.statuses?.[session.statuses.length - 1]?.value]}
                  </Table.Cell>
                  <Table.Cell>
                    <QualityScoreBadge score={session.qualityScore} />
                  </Table.Cell>
                  <Table.Cell className="flex gap-4">
                    <IconButton
                      onClick={() => onSessionModalOpen(session._id)}
                      icon={<SettingsIcon />}
                      size="small"
                      variant="ghost"
                    />
                    <Popconfirm
                      zIndex={99999}
                      title="Are you sure you want to delete this session?"
                      onConfirm={() =>
                        deleteSession({
                          enrollmentId: enrollmentId,
                          sessionId: session._id,
                        })
                      }
                      okText="Yes"
                      cancelText="No"
                    >
                      <IconButton
                        variant="ghost"
                        size="small"
                        color="danger"
                        icon={<DeleteIcon />}
                      />
                    </Popconfirm>
                  </Table.Cell>
                </Table.Row>
              ))}
            </React.Fragment>
          ))}
        </Table.Body>
      </Table>

      <AssignSessionsMentorModal
        isOpen={isAssignModalOpen}
        enrollmentId={enrollmentId}
        sessions={selectedSessions.map((session) => ({
          _id: session._id,
          index: session.index,
        }))}
        primaryMentorName={mentor?.fullName}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={() => setSelectedSessionIds([])}
      />
    </>
  );
};

export default SessionsList;
